import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { SaveIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/newRedesign'
import { Button } from '@/components/newRedesign'
import { Form } from '@/components/ui/form'
import { FormInput } from '@/components/newRedesign/form'
import { FormTextarea } from '@/components/newRedesign/form'

export interface EditDocumentFormData {
  id?: number
  name?: string
  observations?: string
  originalExtension?: string
}

interface EditDocumentDialogProps {
  open: boolean
  onOpenChange: (_open: boolean) => void
  onSave: (_data: EditDocumentFormData) => void
  isPending?: boolean
  initialData?: Partial<EditDocumentFormData>
}

export function EditDocumentDialog({
  open,
  onOpenChange,
  isPending = false,
  initialData,
  onSave,
}: EditDocumentDialogProps) {
  const form = useForm<EditDocumentFormData>({
    defaultValues: {
      name: '',
      observations: '',
    },
  })

  // Update form when dialog opens
  useEffect(() => {
    if (open && initialData) {
      form.reset({
        id: initialData.id,
        name: initialData.name || '',
        observations: initialData.observations || '',
      })
    }
  }, [open, initialData, form])

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      form.reset()
    }
  }, [open, form])

  const handleSubmit = (data: EditDocumentFormData) => {
    onSave({
      ...data,
      id: initialData?.id,
    })
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent
        className={cn(
          'flex max-h-[85vh] w-full max-w-[95vw] flex-col',
          'overflow-hidden bg-[#FAFAFA] p-0',
          'sm:max-w-[90vw] md:max-w-[600px]'
        )}
      >
        <DialogHeader
          className={cn(
            'flex h-[52px] shrink-0 flex-row items-center justify-between',
            'border-b border-[#D4D4D4] px-4 py-0'
          )}
        >
          <DialogTitle className="text-xl font-semibold leading-[24px] tracking-[-0.02em] text-[#262626]">
            Editar documento
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <div className="flex max-h-[calc(85vh-120px)] flex-1 flex-col gap-4 overflow-y-auto bg-[#FAFAFA] px-4 py-4">
            {/* Name Input */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                Nombre
              </label>
              <FormInput
                control={form.control}
                name="name"
                placeholder="Cambiar el nombre del documento"
                showLabel={false}
              />
            </div>

            {/* Observations */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                Observaciones
              </label>
              <FormTextarea
                control={form.control}
                name="observations"
                placeholder="Observaciones"
                rows={4}
                showLabel={false}
              />
            </div>
          </div>

          <DialogFooter
            className={cn(
              'flex h-auto shrink-0 items-center justify-end',
              'border-t border-[#D4D4D4] px-3 py-3',
              'sm:h-[68px] sm:px-4 sm:py-0'
            )}
          >
            <Button
              className={cn(
                'flex h-9 w-full items-center justify-center',
                'gap-2 rounded-md bg-[#1E293B] px-4',
                'hover:bg-[#334155]',
                'sm:w-auto sm:min-w-[110px]'
              )}
              disabled={isPending}
              icon={<SaveIcon className="h-4 w-4" />}
              onClick={form.handleSubmit(handleSubmit)}
              type="button"
              variant="default"
            >
              {isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
